import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ApiService from '../../services/api';
import { persistAdminPortalSession } from '../../utils/adminPortalSession';
import { clearClientAuthSession } from '../../utils/clearClientAuthSession';
import { hasClientAuthSession, getClientAccessToken } from '../../utils/sessionAuth';

interface SupportRouteProps {
  children: React.ReactNode;
}

const SupportRoute: React.FC<SupportRouteProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isSupport, setIsSupport] = useState(false);

  useEffect(() => {
    checkSupportStatus();
  }, []);

  const checkSupportStatus = async () => {
    if (!hasClientAuthSession()) {
      setIsSupport(false);
      setChecking(false);
      return;
    }

    try {
      const response = await ApiService.request('/auth/me');
      const user = response?.data?.user;
      const role = user?.role;
      const hasSupportRole = role === 'support';
      const token = getClientAccessToken();

      if (hasSupportRole && user) {
        persistAdminPortalSession(user, token);
      }

      setIsSupport(hasSupportRole);

      if (!hasSupportRole) {
        localStorage.removeItem('adminUser');
      }
    } catch (error: unknown) {
      console.error('Support session validation failed:', error);
      const status = (error as { response?: { status?: number }; status?: number })?.response?.status
        ?? (error as { status?: number })?.status;
      // Authenticated non-support users must keep their MSME session.
      if (status === 401 || !hasClientAuthSession()) {
        clearClientAuthSession();
      }
      setIsSupport(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isSupport) {
    return <Navigate to={hasClientAuthSession() ? '/dashboard' : '/login'} replace />;
  }

  return <>{children}</>;
};

export default SupportRoute;
